// pages/Personal Attendance/ApprovalsPage.js
//
// Shared approval workspace for FTO and COA, used by BOTH supervisors and HR.
// Linked from the HR Dashboard and the Supervisor menu. Kept OUT of the
// "Personal Attendance Change" hub, which is now personal-only.
//
// SERVER-SIDE PAGED: each queue fetches one page (default 25 rows) from the
// API with page/pageSize/q/status params, so a large company-wide HR queue
// never ships every row over the wire. The API returns { items, total, page,
// pageSize }. A debounced search box + status dropdown drive q/status; paging
// controls drive page.

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Card, CardBody, Spinner, Table, Nav, NavItem, NavLink, Container, Input, Button,
} from "reactstrap";
import { toast } from "react-toastify";
import useAuth from "./useAuth";
import { DashboardPageHeader, useDebouncedValue } from "../../Components/Common/DashboardKit";
import {
    getAwaitingApprovalPaged, getAwaitingHrApprovalPaged, updateFtoApprovals,
    getCoaAwaitingApprovalPaged, getCoaAwaitingHrApprovalPaged, updateCoaApprovals,
} from "./ftoService";

const PAGE_SIZE = 25;

const fmtDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return isNaN(dt) ? String(d) : dt.toLocaleDateString();
};

const ApprovalBadge = ({ val }) =>
    val === true ? <span className="text-success">Approved</span>
        : val === false ? <span className="text-danger">Denied</span>
            : <span className="text-warning">Pending</span>;

// A single server-paged queue (one gate, one kind). Owns its page/search/status
// state and refetches whenever any of them change.
const PagedQueue = ({ fetchPaged, updateFn, gate, onOpen }) => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const debouncedQ = useDebouncedValue(q, 350);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const load = useCallback(() => {
        setLoading(true);
        fetchPaged({ page, pageSize: PAGE_SIZE, q: debouncedQ || undefined, status: status || undefined })
            .then(({ data }) => {
                if (Array.isArray(data)) { setRows(data); setTotal(data.length); }
                else { setRows(data?.items ?? []); setTotal(data?.total ?? 0); }
            })
            .catch(() => { setRows([]); setTotal(0); })
            .finally(() => setLoading(false));
    }, [fetchPaged, page, debouncedQ, status]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [debouncedQ, status]);

    const decide = async (id, approved) => {
        try {
            const payload = gate === "hr" ? { hrApproval: approved } : { supervisorApproval: approved };
            await updateFn(id, payload);
            toast.success(approved ? "Approved." : "Rejected.");
            load();
        } catch {
            toast.error("Could not update. You may not have rights on this request.");
        }
    };

    return (
        <>
            <div className="d-flex flex-wrap gap-2 mb-2">
                <Input bsSize="sm" style={{ maxWidth: 280 }}
                    placeholder="Search ID / EmpID / name / comment…"
                    value={q} onChange={(e) => setQ(e.target.value)} />
                <Input type="select" bsSize="sm" style={{ maxWidth: 160 }}
                    value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="">All statuses</option>
                    <option value="approved">Has Approved</option>
                    <option value="pending">Has Pending</option>
                    <option value="denied">Has Denied</option>
                </Input>
            </div>

            {loading ? (
                <div className="text-center py-3"><Spinner size="sm" /> Loading…</div>
            ) : rows.length === 0 ? (
                <div className="text-muted py-3" style={{ fontSize: 14 }}>Nothing pending here. 🎉</div>
            ) : (
                <div className="table-responsive">
                    <Table className="table-striped table-bordered align-middle mb-0">
                        <thead className="table-light">
                            <tr>
                                <th style={{ fontSize: 12 }}>#</th>
                                <th style={{ fontSize: 12 }}>EmpID</th>
                                <th style={{ fontSize: 12 }}>Name</th>
                                <th style={{ fontSize: 12 }}>Start</th>
                                <th style={{ fontSize: 12 }}>End</th>
                                <th style={{ fontSize: 12 }}>Days</th>
                                <th style={{ fontSize: 12 }}>Supervisor</th>
                                <th style={{ fontSize: 12 }}>HR</th>
                                <th style={{ fontSize: 12 }}>Comment</th>
                                <th style={{ fontSize: 12 }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id ?? `${r.empId}-${r.startDate}`}>
                                    <td>
                                        <a href="#open" onClick={(e) => { e.preventDefault(); r.id && onOpen(r.id); }}>
                                            {r.id}
                                        </a>
                                    </td>
                                    <td>{r.empId}</td>
                                    <td>{r.empName ?? "—"}</td>
                                    <td>{fmtDate(r.startDate)}</td>
                                    <td>{fmtDate(r.endDate)}</td>
                                    <td>{r.ftoCount ?? r.days ?? "—"}</td>
                                    <td><ApprovalBadge val={r.supervisorApproval} /></td>
                                    <td><ApprovalBadge val={r.hrApproval} /></td>
                                    <td style={{ maxWidth: 220 }} className="text-truncate" title={r.employeeComment ?? ""}>
                                        {r.employeeComment ?? "—"}
                                    </td>
                                    <td>
                                        <div className="d-flex gap-1">
                                            <button className="btn btn-sm btn-soft-success" title="Approve"
                                                onClick={() => decide(r.id, true)}>
                                                <i className="ri-check-line" />
                                            </button>
                                            <button className="btn btn-sm btn-soft-danger" title="Reject"
                                                onClick={() => decide(r.id, false)}>
                                                <i className="ri-close-line" />
                                            </button>
                                            <button className="btn btn-sm btn-soft-secondary" title="Open"
                                                onClick={() => r.id && onOpen(r.id)}>
                                                <i className="ri-eye-line" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}

            <div className="d-flex align-items-center justify-content-between mt-2">
                <span style={{ fontSize: 12, color: "#878a99" }}>
                    {total === 0 ? "0 results" :
                        `Showing ${(page - 1) * PAGE_SIZE + 1}-${Math.min(page * PAGE_SIZE, total)} of ${total}`}
                </span>
                <div className="d-flex gap-1">
                    <Button size="sm" color="light" disabled={page <= 1 || loading}
                        onClick={() => setPage(1)}>&laquo;</Button>
                    <Button size="sm" color="light" disabled={page <= 1 || loading}
                        onClick={() => setPage(page - 1)}>&lsaquo;</Button>
                    <span style={{ fontSize: 12, padding: "4px 8px" }}>Page {page} / {totalPages}</span>
                    <Button size="sm" color="light" disabled={page >= totalPages || loading}
                        onClick={() => setPage(page + 1)}>&rsaquo;</Button>
                    <Button size="sm" color="light" disabled={page >= totalPages || loading}
                        onClick={() => setPage(totalPages)}>&raquo;</Button>
                </div>
            </div>
        </>
    );
};

// One kind (FTO or COA) — holds its supervisor + HR sub-queues with a sub-tab.
const KindCard = ({ title, kind, canSupervise, canHr, navigate }) => {
    const [tab, setTab] = useState(canSupervise ? "sup" : "hr");

    const supFetch = kind === "COA" ? getCoaAwaitingApprovalPaged : getAwaitingApprovalPaged;
    const hrFetch = kind === "COA" ? getCoaAwaitingHrApprovalPaged : getAwaitingHrApprovalPaged;
    const updateFn = kind === "COA" ? updateCoaApprovals : updateFtoApprovals;

    const openRequest = (id) =>
        navigate(kind === "COA" ? `/attendance/coa/${id}` : `/attendance/fto/${id}`);

    return (
        <Card className="mb-4">
            <CardBody>
                <h5 className="mb-3">{title}</h5>

                {(canSupervise && canHr) && (
                    <Nav tabs className="mb-3">
                        <NavItem>
                            <NavLink className={tab === "sup" ? "active" : ""}
                                style={{ cursor: "pointer" }} onClick={() => setTab("sup")}>
                                Awaiting My Approval
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink className={tab === "hr" ? "active" : ""}
                                style={{ cursor: "pointer" }} onClick={() => setTab("hr")}>
                                Awaiting HR
                            </NavLink>
                        </NavItem>
                    </Nav>
                )}

                {canSupervise && (!canHr || tab === "sup") && (
                    <>
                        {!canHr && <p className="text-muted mb-2" style={{ fontSize: 12 }}>Awaiting my approval</p>}
                        <PagedQueue fetchPaged={supFetch} updateFn={updateFn} gate="sup" onOpen={openRequest} />
                    </>
                )}

                {canHr && (!canSupervise || tab === "hr") && (
                    <>
                        {!canSupervise && <p className="text-muted mb-2" style={{ fontSize: 12 }}>Awaiting HR approval</p>}
                        <PagedQueue fetchPaged={hrFetch} updateFn={updateFn} gate="hr" onOpen={openRequest} />
                    </>
                )}
            </CardBody>
        </Card>
    );
};

const ApprovalsPage = () => {
    const navigate = useNavigate();
    const auth = useAuth();

    const canSupervise = !!(auth?.isManager || auth?.isSuperUser);
    const canHr = !!(auth?.isHR || auth?.isItAdmin || auth?.isSuperUser);

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-check-double-line"
                    eyebrow="Personal Attendance"
                    title="FTO / COA Approvals"
                    subtitle="Requests waiting for your action — by employee"
                    tone="violet"
                    breadcrumb={[
                        { label: "Dashboards", to: "/" },
                        { label: "Attendance", to: "/workflows?wf=pac" },
                        { label: "Approvals" },
                    ]}
                />

                {!canSupervise && !canHr ? (
                    <Card><CardBody>
                        <div className="text-muted">You don't have approval rights.</div>
                    </CardBody></Card>
                ) : (
                    <>
                        <KindCard title="FTO Approvals" kind="FTO"
                            canSupervise={canSupervise} canHr={canHr} navigate={navigate} />
                        <KindCard title="COA Approvals" kind="COA"
                            canSupervise={canSupervise} canHr={canHr} navigate={navigate} />
                    </>
                )}
            </Container>
        </div>
    );
};

export default ApprovalsPage;
